// --------------------------------------------------------------
// src/UserProfile/ParentProfile/ParentsDashboard.js
// --------------------------------------------------------------
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ParentsDashboard.css";

import { collection, doc, getDoc, query, where, onSnapshot, orderBy} from "firebase/firestore";
import { getAuth, onAuthStateChanged, updatePassword, EmailAuthProvider, reauthenticateWithCredential} from "firebase/auth";
import { db } from "../../firebase";

const FUNCTIONS_BASE = process.env.REACT_APP_FUNCTIONS_URL || "";

const analysePerformance = (raw) => {
  const strong = [];
  const weak = [];
  Object.entries(raw).forEach(([s, v]) => {
    const pct = Math.round((v.finalQuizScore / v.finalQuizTotal) * 100);
    if (pct >= 80) strong.push(s);
    else if (pct < 50) weak.push(s);
  });
  return { strong, weak };
};

const genRandomPwd = () => Math.random().toString(36).slice(-8);

export default function ParentsDashboard() {
  const navigate = useNavigate();
  const [parent, setParent] = useState(null);
  const [kids, setKids] = useState([]);
  const [perf, setPerf] = useState({});
  const [err, setErr] = useState("");
  const [parentPracticeRecords, setParentPracticeRecords] = useState([]);

  const [showPwdForm, setShowPwdForm] = useState(false);
  const [currPwd, setCurrPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), async (user) => {
      if (!user) return setErr("Not logged in.");
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (!snap.exists() || snap.data().accountType !== "Parent")
          return setErr("Access denied: parent only.");
        setParent({ uid: user.uid, ...snap.data() });
      } catch {
        setErr("Failed to load parent profile.");
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    const parentCode = parent?.parentCode;
    if (!parentCode) return;

    setKids([]);
    const kidsQ = query(
      collection(db, "users"),
      where("parentCode", "==", parentCode),
      where("accountType", "==", "Kid"),
      orderBy("firstName")
    );

    const unsubKids = onSnapshot(kidsQ, (snap) => {
      const arr = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
      setKids(arr);

      arr.forEach((kid) => {
        const quizCol = collection(db, "users", kid.uid, "quizResults");
        onSnapshot(quizCol, (qs) => {
          const res = {};
          qs.forEach((d) => (res[d.id] = d.data()));
          setPerf((p) => ({ ...p, [kid.uid]: res }));
        });
      });
    });
    return unsubKids;
  }, [parent?.parentCode]);

  useEffect(() => {
    if (!parent?.uid) return;
    const q = query(
      collection(db, "users", parent.uid, "practiceRecords"),
      orderBy("timestamp", "desc")
    );
    const unsub = onSnapshot(q, snap => {
      setParentPracticeRecords(snap.docs.map(d => d.data()));
    });
    return unsub;
  }, [parent?.uid]);

  const handleParentPwdChange = async () => {
    setErr("");
    if (!currPwd || !newPwd || !confirm) return setErr("All fields required.");
    if (newPwd !== confirm) return setErr("New passwords do not match.");
    if (newPwd.length < 6) return setErr("Password ≥ 6 chars");

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const cred = EmailAuthProvider.credential(user.email, currPwd);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, newPwd);
      alert("Password updated!");
      setShowPwdForm(false);
      setCurrPwd("");
      setNewPwd("");
      setConfirm("");
    } catch (e) {
      setErr(e.message.replace("Firebase:", "").trim());
    }
  };

  if (err)
    return (
      <div className="parent-dashboard-page">
        <p>{err}</p>
      </div>
    );
  if (!parent)
    return (
      <p style={{ color: "white", textAlign: "center" }}>Loading…</p>
    );

  // --- **新版 Flex 排列的主要區塊** ---
  return (
    <div className="parent-dashboard-page">
      <h2 className="parent-dashboard-title">Parent Dashboard</h2>

      <section className="parent-info-section">
        <h3>
          Welcome, {parent.firstName} {parent.lastName}!
        </h3>
        <p>
          Your Parent Code: <strong>{parent.parentCode}</strong>
        </p>

        <div className="parent-password-block">
          <h4>Account Security</h4>
          {!showPwdForm ? (
            <button
              className="change-parent-pwd-btn"
              onClick={() => setShowPwdForm(true)}
            >
              Change My Password
            </button>
          ) : (
            <form className="parent-pwd-form" onSubmit={(e) => { e.preventDefault(); handleParentPwdChange(); }} >
              <input
                type="text"
                name="username"
                autoComplete="username"
                value={parent.accountName}
                readOnly hidden
              />
              <input
                type="password"
                placeholder="Current password"
                value={currPwd}
                onChange={(e) => setCurrPwd(e.target.value)}
                autoComplete="current-password"
                required
              />
              <input
                type="password"
                placeholder="New password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoComplete="new-password"
                required
              />
              <input
                type="password"
                placeholder="Confirm new"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button type="submit">Save</button>
              <button type="button" onClick={() => setShowPwdForm(false)}>Cancel</button>
            </form>
          )}
        </div>
      </section>

      {/* --- FLEX ROW START --- */}
      <div className="dashboard-main-flex">
        <section className="kids-overview-section">
          <h3>Your Kids:</h3>
          {kids.length === 0 ? (
            <div className="no-kid-block">
              <p>No kids linked to your code yet.</p>
            </div>
          ) : (
            kids.map((k) => (
              <KidBlock key={k.uid} kid={k} data={perf[k.uid] || {}} />
            ))
          )}
          <div style={{ marginTop: "18px" }}>
            <button
              className="signup-kid-btn"
              onClick={() => navigate("/signup/kid")}
            >
              ＋ Set up another kid’s account
            </button>
          </div>
        </section>
        {/* ---------- 家長帳號自己的學習紀錄（在右側） ---------- */}
        <section className="parent-activity-section">
  <h3>Your Own Practice Records</h3>
  {parentPracticeRecords.length === 0 ? (
    <div className="no-practice-block">
      <p>No practice records yet. Start exploring lessons or quizzes!</p>
    </div>
  ) : (
    (() => {
      // 1. 取每個 topic/toolType 的最新紀錄
      const latestRecordsByTopic = {};
      parentPracticeRecords.forEach((rec) => {
        const key = rec.topic || rec.toolType || "other";
        if (
          !latestRecordsByTopic[key] ||
          (rec.timestamp && latestRecordsByTopic[key].timestamp && rec.timestamp.seconds > latestRecordsByTopic[key].timestamp.seconds)
        ) {
          latestRecordsByTopic[key] = rec;
        }
      });
      const latestRecords = Object.values(latestRecordsByTopic);

      return (
        <div className="practice-record-table-container">
          <table className="practice-record-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Correct</th>
                <th>Total</th>
                <th>Accuracy</th>
                <th>Time Spent</th>
              </tr>
            </thead>
            <tbody>
              {latestRecords.map((rec, idx) => (
                <tr key={idx}>
                  <td>
                    {rec.timestamp && rec.timestamp.toDate
                      ? rec.timestamp.toDate().toLocaleString()
                      : "-"}
                  </td>
                  <td>{rec.topic || rec.toolType || "-"}</td>
                  <td>{rec.correct ?? "-"}</td>
                  <td>{rec.numQuestions ?? "-"}</td>
                  <td>
                    {(rec.correct !== undefined && rec.numQuestions)
                      ? (
                        <span style={{
                          color: rec.correct / rec.numQuestions > 0.8 ? "#22a07b"
                            : rec.correct / rec.numQuestions > 0.5 ? "#df9c2e"
                            : "#c82333",
                          fontWeight: 600
                        }}>
                          {Math.round(100 * rec.correct / rec.numQuestions)}%
                        </span>
                      ) : "-"}
                  </td>
                  <td>{rec.durationSec ? rec.durationSec + "s" : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    })()
  )}
          {parentPracticeRecords.length > 0 && (
            <div className="remind-switch-kid-block" style={{marginTop: 12, background: "#ffe7c7", borderRadius: 8, padding: 14}}>
              <p>
                <strong>For a better experience:</strong> If you want your child to earn pets, badges, and get a personalized progress report, please <b>switch to their account</b> before practicing!
              </p>
              <button
                style={{marginTop: 8, background: "#2c5777", color: "#fff", borderRadius: 6, padding: "8px 20px", fontWeight: 600, cursor: "pointer"}}
                onClick={() => navigate("/switch-account")}
              >
                Switch to Kid Account
              </button>
            </div>
          )}
        </section>
      </div>
      {/* --- FLEX ROW END --- */}
    </div>
  );
}

/* ---------- KidBlock ---------- */
function KidBlock({ kid, data }) {
  const { strong, weak } = analysePerformance(data);
  const [edit, setEdit] = useState(false);
  const [kidPwd, setKidPwd] = useState("");

  const [practiceRecords, setPracticeRecords] = useState([]);

  useEffect(() => {
    const q = query(
      collection(db, "users", kid.uid, "practiceRecords"),
      orderBy("timestamp", "desc")
    );
    const unsub = onSnapshot(q, snap => {
      setPracticeRecords(snap.docs.map(d => d.data()));
    });
    return unsub;
  }, [kid.uid]);

  const saveKidPwd = async () => {
    if (kidPwd.length < 6) return alert("Password ≥ 6 chars");
    try {
      const idToken = await getAuth().currentUser.getIdToken(true);
      await fetch(`${FUNCTIONS_BASE}/api/updateKidPassword`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ kidUid: kid.uid, newPassword: kidPwd }),
      });
      alert(`Password updated for ${kid.firstName}. New password: ${kidPwd}`);
      setEdit(false);
      setKidPwd("");
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div className="kid-performance-block">
      <h4>
        {kid.firstName} {kid.lastName} ({kid.accountName})
      </h4>
      <p>
        <strong>Strong:</strong> {strong.join(", ") || "None"} &nbsp;
        <strong>Weak:</strong> {weak.join(", ") || "None"}
      </p>

      <table className="kid-performance-table">
        <thead>
          <tr>
            <th>Subject</th>
            <th>Time</th>
            <th>Score</th>
            <th>Total</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(data).map(([s, v]) => {
            const pct = Math.round((v.finalQuizScore / v.finalQuizTotal) * 100);
            return (
              <tr key={s}>
                <td>{s}</td>
                <td>{v.timeSpent ?? "-"}</td>
                <td>{v.finalQuizScore ?? "-"}</td>
                <td>{v.finalQuizTotal ?? "-"}</td>
                <td>
                  <div className="parent-progress-bar">
                    <div
                      className="parent-progress-fill"
                      style={{ width: `${pct}%` }}
                    >
                      {isNaN(pct) ? "-" : `${pct}%`}
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h5 style={{ margin: '18px 0 8px 0', fontSize: 17, fontWeight: 600, color: "#2c5777" }}>
        Recent Math Practice
      </h5>
      {practiceRecords.length === 0 ? (
        <div style={{ color: '#aaa', padding: "8px 0 18px 0" }}>No practice records yet.</div>
      ) : (
        <div className="practice-record-table-container">
          <table className="practice-record-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Correct</th>
                <th>Total</th>
                <th>Accuracy</th>
                <th>Time Spent</th>
              </tr>
            </thead>
            <tbody>
              {practiceRecords.slice(0, 10).map((rec, idx) => (
                <tr key={idx}>
                  <td>
                    {rec.timestamp && rec.timestamp.toDate
                      ? rec.timestamp.toDate().toLocaleString()
                      : "-"}
                  </td>
                  <td>{rec.topic || rec.toolType || "-"}</td>
                  <td>{rec.correct ?? "-"}</td>
                  <td>{rec.numQuestions ?? "-"}</td>
                  <td>
                    {(rec.correct !== undefined && rec.numQuestions)
                      ? (
                        <span style={{
                          color: rec.correct / rec.numQuestions > 0.8 ? "#22a07b"
                            : rec.correct / rec.numQuestions > 0.5 ? "#df9c2e"
                            : "#c82333",
                          fontWeight: 600
                        }}>
                          {Math.round(100 * rec.correct / rec.numQuestions)}%
                        </span>
                      ) : "-"}
                  </td>
                  <td>{rec.durationSec ? rec.durationSec + "s" : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!edit ? (
        <button
          className="kid-editpwd-btn"
          onClick={() => {
            setKidPwd(genRandomPwd());
            setEdit(true);
          }}
        >
          Change Password
        </button>
      ) : (
        <div className="kid-editpwd-form">
          <input
            type="text"
            value={kidPwd}
            onChange={(e) => setKidPwd(e.target.value)}
          />
          <button onClick={() => setKidPwd(genRandomPwd())}>Random</button>
          <button onClick={saveKidPwd}>Save</button>
          <button onClick={() => setEdit(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}
